//@flow strict-local
import {
  ListFilesQuery,
  ListFilesMessage,
  PageRequestData,
  CurrentContextInfo,
  RepoItem,
  ListFilesResponse,
  GetAssetsInfoQuery,
  GetAssetsInfoMessage,
  GetAssetsInfoResponse,
  FileFilters
} from '../../autogenerated/ZoomBridge';
import ErrorId from '../../protocol/ErrorId';
import _ from 'lodash';
import Store from '../../store/Store';
import { CONTEXT_INFO, SERVER_SPEC } from '../../store/Constants';
import Storage from '../../protocol/Storage';
import MetadataUtil from '../../util/MetadataUtil';
import HashMap from '../../protocol/HashMap';
import CollectionUtil from '../lightbox/CollectionUtil';
import StringUtil from '../../util/StringUtil';
const debug = require('../../debug/debug')('wc:queries:asset:AssetListCache');

type ListOptions = {
  fileFilters?: FileFilters,
  force?: boolean,
  nocache?: boolean
};

export const foldersOnly = new FileFilters().setFileTypes(['D']);
export const imagesOnly = new FileFilters().setFileTypes(['IMAGE']);
export const videosOnly = new FileFilters().setFileTypes(['VIDEO']);

export default class AssetListCache {
  /**
   * @private
   */
  static INSTANCE: AssetListCache;
  pageId: string =
    Math.random()
      .toString(36)
      .substr(2) +
    '-' +
    Date.now().toString(36);
  pageLimit: number = 1000;
  rootFuidToMaxRrn: Map<number, number> = new Map();
  rootFuidToRepoItems: Map<number, Map<number, RepoItem>> = new Map();
  fuid2Kids: Map<number, number[]> = new Map();

  constructor() {
    this.init();
  }

  async init() {
    if (!Store.get(SERVER_SPEC)) {
      const f = () => {
        Store.removeListener(SERVER_SPEC, f);
        this.init();
      };

      Store.addListener(SERVER_SPEC, f);
      return;
    }

    debug('[init()] begin');
    if (window.gs.isExternalUser || CollectionUtil.isLink()) {
      debug('[init()] external/link user, return.');
      return;
    }

    const json = Storage.get(CONTEXT_INFO);
    let info;
    if (json) {
      info = new CurrentContextInfo();
      info.deserializeJson(JSON.parse(json));
    } else {
      info = new CurrentContextInfo()
        .setOrder(CollectionUtil.ASSET_SORT_PARAMS.DEFAULT_SORT_ORDER)
        .setSortCriteria(CollectionUtil.ASSET_SORT_PARAMS.DEFAULT_SORT_CRITERIA);
    }

    Store.put(CONTEXT_INFO, info);

    const projectFuids = await this.fetchFuids(0);
    debug('[init()] projectFuids: ', projectFuids);
    const projects = await this.getOrFetchRepoItems(0, projectFuids);
    debug('[init()] projects: ', projects);
  }

  static getInstance(): AssetListCache {
    return this.INSTANCE;
  }

  async getOrFetchRepoItems(rootFuid: number, kids: number[]): Promise<RepoItem[]> {
    try {
      debug('[getOrFetchRepoItems()] args: ', { rootFuid, kids });
      let assets = [];
      let missingFuids = [];
      kids.forEach((fuid) => {
        const asset = this.getKid(rootFuid, fuid);
        if (asset) {
          assets.push(asset);
        } else {
          missingFuids.push(fuid);
        }
      });

      debug('[getOrFetchRepoItems()] assets in cache: ', assets);
      if (missingFuids.length > 0) {
        const missingAssets = await this.fetchRepoItems(rootFuid, missingFuids);
        debug('[getOrFetchRepoItems()] fetched assets: ', missingAssets);
      }

      debug('[getOrFetchRepoItems()] all assets: ', assets);
      return kids.map((k: number) => this.getKid(rootFuid, k));
    } catch (err) {
      debug('[getOrFetchRepoItems()] error: ', err);
      throw err;
    }
  }

  fetchRepoItems(rootFuid: number, kids: number[]): Promise<RepoItem[]> {
    debug('[fetchRepoItems()] args: ', { rootFuid, kids });
    const fuidToRepoItems: ?Map<number, RepoItem> = this.rootFuidToRepoItems.get(rootFuid);
    var noAvailableFuids = undefined;
    if (rootFuid > 0) {
      noAvailableFuids = fuidToRepoItems
        ? _.filter(kids, (fuid) => {
            return !fuidToRepoItems.has(fuid);
          })
        : kids;
    } else {
      //For root fuid, no need to send any fuid. Server will send all the project fuids
      noAvailableFuids = undefined;
    }

    debug('[fetchRepoItems()] noAvailableFuids: ', noAvailableFuids);
    let getAssetInfo: GetAssetsInfoQuery = undefined;
    if (rootFuid === 0 || _.size(noAvailableFuids) > 0) {
      //Root fuid zero mean send the cal.
      const message: GetAssetsInfoMessage = new GetAssetsInfoMessage()
        .setRootFuid(rootFuid)
        .setMaxRrn(this.getMaxRrn(rootFuid))
        .setFuids(noAvailableFuids)
        .setPageId(this.pageId);
      getAssetInfo = new GetAssetsInfoQuery();
      getAssetInfo.message = message;
    }
    return new Promise((resolve, reject) => {
      if (getAssetInfo) {
        getAssetInfo.run().then(
          (response: GetAssetsInfoResponse) => {
            debug('[fetchRepoItems()] response: ', { query: getAssetInfo, response });
            this.updateCacheFromAssetInfoReponse(rootFuid, response);
            resolve(response.getResultFiles());
          },
          (error: ErrorId) => {
            debug('[fetchRepoItems()] error: ', error);
            reject(error);
          }
        );
      } else {
        resolve([]);
      }
    });
  }

  /**
   * @private
   */

  fetchRepoItemByFuid(rootFuid: number, kids: number[]): Promise<RepoItem[]> {
    debug('[fetchRepoItems()] args: ', { rootFuid, kids });
    let getAssetInfo: GetAssetsInfoQuery = undefined;
    const message: GetAssetsInfoMessage = new GetAssetsInfoMessage()
      .setRootFuid(rootFuid)
      .setMaxRrn(this.getMaxRrn(rootFuid))
      .setFuids(kids)
      .setPageId(this.pageId);
    getAssetInfo = new GetAssetsInfoQuery();
    getAssetInfo.message = message;
    return new Promise((resolve, reject) => {
      if (getAssetInfo) {
        getAssetInfo.run().then(
          (response: GetAssetsInfoResponse) => {
            debug('[fetchRepoItems()] response: ', { query: getAssetInfo, response });
            resolve(response.getResultFiles());
          },
          (error: ErrorId) => {
            debug('[fetchRepoItems()] error: ', error);
            reject(error);
          }
        );
      } else {
        resolve([]);
      }
    });
  }

  /**
   * @private
   */
  updateCacheFromAssetInfoReponse(rootFuid: number, response: GetAssetsInfoResponse): void {
    let fuidToRepoItems: ?Map<number, RepoItem> = this.rootFuidToRepoItems.get(rootFuid);
    if (!fuidToRepoItems) {
      this.rootFuidToRepoItems.set(rootFuid, (fuidToRepoItems = new Map()));
    }
    debug('[updateCacheFromAssetInfoReponse()]', rootFuid, response);
    const repoItems: RepoItem[] = response.getResultFiles();
    for (let i = 0; i < repoItems.length; i++) {
      const repoItem = repoItems[i];
      const fuid: number = repoItem.getFuid();
      fuidToRepoItems.set(fuid, repoItem);
      //MetadataUtil.addAssetIdInMetadataMapOfAsset([repoItem]);
    }
  }

  async fetchFuids(rootFuid: number, options: ListOptions = {}): Promise<number[]> {
    //if (options.force === true) {
    debug('[fetchFuids()] args: ', { rootFuid });
    return await this.list(rootFuid, options);
    //} else {
    //      let fuids: ?number[] = this.fuid2Kids.get(rootFuid);
    //return (fuids ? fuids : []);
    //}
  }

  /**
   * Method returns the list of fuids for given directory.
   *
   * options.fileFilters.fileType can be "F" | "D" | "IMAGE" | "VIDEO"
   */
  async list(rootFuid: number, options: ListOptions = {}): Promise<number[]> {
    try {
      debug('[list(rootFuid: %d, options: %o)]', rootFuid, options);
      const { fileFilters, force } = options;

      const listFilesQuery: ListFilesQuery = new ListFilesQuery();
      const info: CurrentContextInfo = _.cloneDeep(Store.get(CONTEXT_INFO));

      //let field = info.getSortCriteria().toLowerCase();
      // if (field !== 'name' && field !== 'kind') {
      //   field = 'mtime';
      // }
      //info.setSortCriteria(field);

      const message: ListFilesMessage = new ListFilesMessage()
        .setFuid(rootFuid)
        .setPageLimit(this.pageLimit)
        .setPageRequestData(this.createPageRequestData())
        .setCurrentContextInfo(info)
        .setMaxRrn(this.getMaxRrn(rootFuid))
        .setForce(force);

      if (options.useCacheOnly) {
        message.setUseCacheOnly(options.useCacheOnly);
      }
      if (fileFilters) {
        const fileTypes: string[] = fileFilters.getFileTypes();
        const filters: HashMap = fileFilters.getFilter2ValueMap();
        if (
          StringUtil.isNotEmpty(fileFilters.getFilterText()) ||
          (fileTypes && fileTypes.length) ||
          (filters && filters.size())
        ) {
          message.setFileFilters(fileFilters);
        }
      }

      listFilesQuery.message = message;
      const response: ListFilesResponse = await listFilesQuery.run();
      debug('[list()] response: %O', {
        query: rootFuid,
        maxRrn: this.getMaxRrn(rootFuid),
        response: response.getFuids()
      });
      if (options.nocache) {
        return response.getFuids();
      } else {
        this.updateCache(rootFuid, response, options);
        let fuids: ?(number[]) = this.fuid2Kids.get(rootFuid);
        return fuids ? fuids : [];
      }
    } catch (err) {
      this.clearCache(rootFuid);
      debug('[list()] error: ', err);
      throw err;
    }
  }

  /**
   * @private
   */
  updateCache(rootFuid: number, listFilesResponse: ListFilesResponse, options: ListOptions = {}): void {
    debug('[updateCache()] args: ', { rootFuid, listFilesResponse, options });
    const { force, nocache } = options;
    if (nocache) {
      return;
    }

    const fuids: number[] = listFilesResponse.getFuids();
    const maxRrn = listFilesResponse.getMaxRrn();
    const totalAssets = listFilesResponse.getTotalAssets();
    const totalPageCount = listFilesResponse.getTotalPageCount();
    const existingMaxRrn = this.getMaxRrn(rootFuid);

    if (force || existingMaxRrn != maxRrn || rootFuid === 0) {
      debug(
        '[updateCache()] existing max rrn ',
        existingMaxRrn,
        'received max rrn',
        maxRrn,
        'rootFuid',
        rootFuid,
        'Reseting values'
      );
      this.rootFuidToMaxRrn.set(rootFuid, maxRrn);
      this.fuid2Kids.set(rootFuid, fuids);
      this.rootFuidToRepoItems.set(rootFuid, new Map());
    }
  }

  clearCache(rootFuid: number): void {
    debug('[clearCache()]', rootFuid);
    this.rootFuidToMaxRrn.delete(rootFuid);
    this.fuid2Kids.delete(rootFuid);
  }

  getMaxRrn(rootFuid: number): number {
    const maxRrn = this.rootFuidToMaxRrn.get(rootFuid);
    return maxRrn ? maxRrn : 0;
  }

  resetMaxRrn(folderFuids: number[]): void {
    folderFuids.forEach((fuid) => {
      this.rootFuidToMaxRrn.set(fuid, 0);
    });
  }

  /**
   * @private
   */
  createPageRequestData(): PageRequestData {
    return new PageRequestData()
      .setPageId(this.pageId)
      .setAbsolutePageNumber(true)
      .setPageNumber(0);
  }

  getKid(rootFuid: number, kid: number): RepoItem {
    const fuid2RepoIem = this.rootFuidToRepoItems.get(rootFuid);
    return fuid2RepoIem ? fuid2RepoIem.get(kid) : undefined;
  }

  getFolderKidsFuid(rootFuid: number): number[] {
    const fuid2RepoIem = this.rootFuidToRepoItems.get(rootFuid);
    let fuids = undefined;
    if (fuid2RepoIem) {
      fuids = [];
      fuid2RepoIem.forEach((v, k) => {
        if (!v.getBasicFileAttributes().isFile()) fuids.push(k);
      });
    }
    return fuids;
  }

  static test() {
    setInterval(function() {
      AssetListCache.getInstance()
        .list(1)
        .then(
          function(fuids: number[]) {
            console.debug('Projects: ', fuids);
            AssetListCache.getInstance()
              .fetchRepoItems(1, fuids)
              .then(
                function(success: boolean) {
                  console.debug('Project Details:', success);
                },
                function(errorId: ErrorId) {
                  console.debug(errorId);
                }
              );
          },
          function() {}
        );
    }, 5000);
  }
}

AssetListCache.INSTANCE = new AssetListCache();
